// ── Content Growth: the adaptive monthly window ──────────────────────────────
//
// Extracted from Dashboard.js so the arithmetic can be tested without rendering
// the Overview tab. It was module-private there, which is why the case QA hit —
// "I generated a single post, it does not show up" — had nothing pinning it.
//
// THE WINDOW — A FIXED FIVE MONTHS
// Always the last five calendar months, ending with the current one. A month
// with no output still gets a column, because "we published nothing in June" is
// information and a chart that silently omits it invites the reader to compare
// two bars that are not adjacent in time.
//
// ── WHY NOT GROW FROM THE FIRST GENERATION ──────────────────────────────────
// It used to anchor on the user's first content month and grow to a cap of
// five. That made the axis mean something different for every account and for
// the same account week to week: an account two months old rendered two fat
// bars filling the card, which reads as "we are at full output" rather than
// "we have been here two months". A fixed axis is comparable with itself over
// time, which is the entire point of a growth chart.
//
// ── anchor IS NO LONGER "IS THE CHART EMPTY?" ───────────────────────────────
// It is the first month this account EVER generated in, which may now sit
// outside the window. The two facts the caller needs are different questions:
//
//   anchor === null            never generated anything  → "No content yet"
//   anchor set, all five zero  has history, none recent  → "Nothing in the
//                                                          last 5 months"
//
// Collapsing those would tell a returning customer with 400 old articles that
// they have never written one.
//
// TIME IS LOCAL, ON PURPOSE
// Buckets are keyed by the viewer's local calendar month, matching how the
// server builds its 'YYYY-MM' keys. Mixing local and UTC here would misfile
// anything generated within a few hours of a month boundary.

export const GROWTH_WINDOW_MONTHS = 5;

/**
 * The last GROWTH_WINDOW_MONTHS calendar months ending at `current`, all zero.
 *
 * `isCurrent` marks the month still in progress — the chart de-emphasises it,
 * because a part-month bar next to four whole ones is not a fall in output.
 */
const emptyWindow = (current) => {
  const months = [];
  for (let i = GROWTH_WINDOW_MONTHS - 1; i >= 0; i--) {
    const d = new Date(current.getFullYear(), current.getMonth() - i, 1);
    months.push({
      key: `${d.getFullYear()}-${d.getMonth()}`,
      label: d.toLocaleDateString('en-US', { month: 'short' }),
      // Named in full for the tooltip: "Jul" alone is ambiguous once the window
      // spans a year boundary.
      fullLabel: d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      value: 0,
      isCurrent: i === 0,
    });
  }
  return months;
};

/**
 * The empty chart: no anchor, no months.
 *
 * Replaces buildContentGrowth([]), which was the only surviving use of a
 * FALLBACK path that has been removed. That function bucketed raw
 * user_generations rows client-side and filtered them on `g.credit_type` — a
 * field that does not exist on those rows, so `!g.credit_type` was true for
 * every one and the filter passed EVERYTHING, counting AI images as articles.
 * It was never reached in practice because the path that called it was broken
 * in its own way first; deleting it removes a wrong answer waiting to be given.
 */
export const EMPTY_GROWTH = Object.freeze({ anchor: null, months: [] });

/**
 * PRIMARY path: build the window from pre-aggregated monthly counts,
 * [{ month: 'YYYY-MM', count: N }, …] sorted ascending.
 *
 * Preferred over the raw-row path because it carries no row-count ceiling: the
 * old client-side aggregation read at most 1000 generations, which truncated
 * both the anchor and the totals for prolific accounts.
 *
 * @param now injectable so the window can be tested at a fixed date.
 */
export const buildContentGrowthFromAggregated = (monthCounts = [], now = new Date()) => {
  const current = new Date(now.getFullYear(), now.getMonth(), 1);
  // Built first and returned whatever happens: the five columns are the axis,
  // and they exist even for an account with nothing in them.
  const months = emptyWindow(current);

  const rows = (monthCounts || []).filter((r) => {
    const [y, m] = String(r?.month).split('-').map(Number);
    return Number.isFinite(y) && Number.isFinite(m) && m >= 1 && m <= 12;
  });
  if (rows.length === 0) return { anchor: null, months };

  const byKey = Object.fromEntries(months.map((m) => [m.key, m]));
  // The anchor is the EARLIEST month with output, taken across every row rather
  // than trusting the first — it may sit outside the window, and it is the only
  // thing that distinguishes "never published" from "not published lately".
  let anchor = null;
  rows.forEach(({ month, count }) => {
    const [y, m] = String(month).split('-').map(Number);
    const at = new Date(y, m - 1, 1);
    if (anchor === null || at < anchor) anchor = at;
    const bucket = byKey[`${y}-${m - 1}`];
    if (bucket) bucket.value += Number(count) || 0;
  });

  return { anchor, months };
};

/**
 * The window chip's label.
 *
 * 0 returns null — there is no window to name — and the caller hides the chip,
 * which is what stops an account with no chart advertising a five-month one.
 *
 * The window is fixed now, so the Dashboard only ever passes 0 or 5. The
 * in-between branches are kept because this is a pure label function and a
 * caller with a shorter series should still get an honest name for it, not a
 * chip claiming five months over three bars.
 */
export const growthLabelFor = (n) => {
  if (!n) return null;
  if (n === 1) return 'This month';
  if (n < GROWTH_WINDOW_MONTHS) return `Last ${n} months`;
  return `Last ${GROWTH_WINDOW_MONTHS} months`;
};

/**
 * "just now" / "3 min ago" — how fresh the reading is, in words.
 *
 * @param now injectable for tests.
 */
export const relativeTime = (date, now = Date.now()) => {
  if (!date) return null;
  const seconds = Math.max(0, Math.round((now - date.getTime()) / 1000));
  if (seconds < 45) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
};
